import React from "react";
import { Box } from "@mui/material";
import { WebhookAuthSummary } from "../../../common/WebhookAuthFields";
import {
  Card,
  CardHeader,
  Hint,
  Mono,
  Pill,
  SectionLabel,
  TS_COLORS,
  TsButton,
} from "../../Troubleshooting/TroubleshootingUi";
import { MULISH } from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { canEditSiteWebhooks } from "./SiteAboutPageUtils/siteWebhooks";
import {
  WebhookLink,
  fetchSiteWebhookLinks,
  readSiteCameraNames,
  streamLabel,
} from "./SiteAboutPageUtils/webhookLinks";
import { EVENT_TYPE_LABEL } from "./SiteAboutPageUtils/webhookPayload";
import { WebhookLinkTestButton } from "./WebhookLinkTestButton";

export const WEBHOOKS_TAB_TITLES = [
  "Display Name",
  "Webhook Type",
  "Method",
  "Target URL",
  "Message Body",
  "Authentication",
];

const Detail = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ minWidth: 0 }}>
    <SectionLabel>{label}</SectionLabel>
    <Box sx={{ ...MULISH, fontSize: 14, mt: 0.5, wordBreak: "break-all" }}>
      {children}
    </Box>
  </Box>
);

const LinkList = ({
  links,
  cameraNames,
  canTest,
}: {
  links: WebhookLink[];
  cameraNames: Record<number, string>;
  canTest: boolean;
}) => {
  if (links.length === 0) {
    return <Hint>No cameras send to this webhook yet.</Hint>;
  }
  return (
    <Box
      component="ul"
      sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 0.75 }}
    >
      {links.map((link) => (
        <Box
          component="li"
          key={link.id}
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ ...MULISH, fontSize: 14 }}>
            {streamLabel(link.stream, cameraNames)}
          </Box>
          {(link.event_types ?? []).length === 0 ? (
            <Pill tone="warn">No events</Pill>
          ) : (
            link.event_types.map((value) => (
              <Pill key={value} tone="info">
                {EVENT_TYPE_LABEL[value] ?? value}
              </Pill>
            ))
          )}
          {link.custom_fields && Object.keys(link.custom_fields).length > 0 && (
            <Pill tone="grey" title={JSON.stringify(link.custom_fields)}>
              Custom fields
            </Pill>
          )}
          {canTest && (
            <WebhookLinkTestButton
              linkId={link.id}
              label={streamLabel(link.stream, cameraNames)}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

/**
 * Site About → Webhooks (ENG-2233, revamped for ENG-2427): one card per site
 * webhook with its destination, payload template and the cameras that send
 * to it. Add / Edit open WebhookSetupDialog and Delete opens
 * DeleteSiteWebhookDialog, both through the site-about store so the many
 * tab-render call sites don't need more props.
 */
export const WebhooksTab = ({ webhooksInfo }: { webhooksInfo: any }) => {
  const setAddSiteWebhookDialogOpen = useSiteAboutStore(
    (state) => state.setAddSiteWebhookDialogOpen
  );
  const setEditSiteWebhookId = useSiteAboutStore(
    (state) => state.setEditSiteWebhookId
  );
  const setDeleteSiteWebhookId = useSiteAboutStore(
    (state) => state.setDeleteSiteWebhookId
  );
  const webhooks: any[] = Array.isArray(webhooksInfo) ? webhooksInfo : [];
  const canEdit = canEditSiteWebhooks();
  const siteId: number | undefined = webhooks[0]?.customer;

  const [links, setLinks] = React.useState<WebhookLink[] | null>(null);
  const [linksFailed, setLinksFailed] = React.useState(false);
  const cameraNames = React.useMemo(readSiteCameraNames, [webhooksInfo]);

  React.useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    setLinksFailed(false);
    fetchSiteWebhookLinks(siteId).then(
      (siteLinks) => {
        if (!cancelled) setLinks(siteLinks);
      },
      () => {
        if (!cancelled) setLinksFailed(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [siteId, webhooksInfo]);

  const addButton = (
    <TsButton onClick={() => setAddSiteWebhookDialogOpen(true)}>
      + Add Webhook
    </TsButton>
  );

  if (webhooks.length === 0) {
    return (
      <Card>
        <Box
          className="flex flex-col items-center gap-3 py-10"
          data-testid="webhooks-tab-empty"
        >
          <p className="font-mulish text-[#283E6D] text-[20px] text-center m-0">
            No webhooks configured for this site.
          </p>
          {canEdit && (
            <Hint>
              Add one to send this site&apos;s alerts to your own system.
            </Hint>
          )}
          {canEdit && addButton}
        </Box>
      </Card>
    );
  }

  return (
    <Box className="flex flex-col gap-4 p-[10px]">
      {canEdit && (
        <div className="flex justify-end" data-testid="webhooks-tab-actions">
          {addButton}
        </div>
      )}
      {webhooks.map((webhook: any) => {
        const ownLinks = (links ?? []).filter(
          (link) => link.webhook?.id === webhook.id
        );
        return (
          <Card key={webhook.id} testId={`site-webhook-row-${webhook.id}`}>
            <CardHeader
              title={webhook.display_name}
              hint={
                <Mono>{`${webhook.method || "POST"} ${
                  webhook.target_url
                }`}</Mono>
              }
              right={
                canEdit && (
                  <>
                    <TsButton
                      size="sm"
                      variant="outline"
                      aria-label={`Edit ${webhook.display_name}`}
                      onClick={() => setEditSiteWebhookId(webhook.id)}
                    >
                      Edit
                    </TsButton>
                    <TsButton
                      size="sm"
                      variant="ghost"
                      color={TS_COLORS.red}
                      aria-label={`Delete ${webhook.display_name}`}
                      onClick={() => setDeleteSiteWebhookId(webhook.id)}
                    >
                      Delete
                    </TsButton>
                  </>
                )
              }
            />
            <Box
              sx={{
                p: 2.5,
                backgroundColor: "#fff",
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1fr) minmax(0, 1fr)",
                },
                gap: 2.5,
              }}
            >
              <Box sx={{ display: "grid", gap: 2, alignContent: "start" }}>
                <Detail label="Authentication">
                  <WebhookAuthSummary webhook={webhook} />
                </Detail>
                <Detail label="Payload template">
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      fontFamily: "monospace",
                      fontSize: 12.5,
                      whiteSpace: "pre-wrap",
                      maxHeight: 160,
                      overflow: "auto",
                    }}
                  >
                    {webhook.message_body || "{}"}
                  </Box>
                </Detail>
              </Box>
              <Detail label="Cameras">
                {linksFailed ? (
                  <Hint>
                    Couldn&apos;t load which cameras use this webhook.
                  </Hint>
                ) : links === null ? (
                  <Hint>Loading…</Hint>
                ) : (
                  <LinkList
                    links={ownLinks}
                    cameraNames={cameraNames}
                    canTest={canEdit}
                  />
                )}
              </Detail>
            </Box>
          </Card>
        );
      })}
    </Box>
  );
};
